import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/i18n/LanguageContext";
import { ArrowRight, Code, Shield, Server, MapPin, Mail, Github, Facebook } from "lucide-react";

const AboutPage = () => {
  const { t } = useLanguage();

  const values = [
    { icon: Code, titleKey: "about.excellence", descKey: "about.excellenceDesc" },
    { icon: Shield, titleKey: "about.securityValue", descKey: "about.securityValueDesc" },
    { icon: Server, titleKey: "about.performance", descKey: "about.performanceDesc" },
  ];

  return (
    <div className="min-h-screen pt-16 md:pt-20" data-testid="about-page">
      <section className="py-12 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} className="relative order-2 lg:order-1">
              <div className="relative aspect-square max-w-md mx-auto">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-3xl blur-2xl opacity-20" />
                <div className="relative w-full h-full rounded-3xl overflow-hidden border border-border">
                  <img src="https://customer-assets.emergentagent.com/job_devportfolio-122/artifacts/ev4aohwd_20250912_192904.jpg" alt="Tsitsok Michel Berger" className="w-full h-full object-cover" />
                </div>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.1 }} className="order-1 lg:order-2">
              <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-4 md:mb-6">
                {t("about.title")} <span className="gradient-text-blue">{t("about.me")}</span>
              </h1>
              
              <p className="text-muted-foreground mb-4 text-sm md:text-base leading-relaxed">
                {t("about.intro")} <strong className="text-foreground">Tsitsok Michel Berger</strong>, {t("about.intro2")} <strong className="text-foreground">Cameroun</strong>. {t("about.intro3")}
              </p>
              
              <p className="text-muted-foreground mb-6 text-sm md:text-base leading-relaxed">
                {t("about.expertise")}
              </p>

              <div className="flex flex-wrap gap-4 mb-6 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <MapPin className="h-4 w-4 text-primary" />
                  <span>Cameroun</span>
                </div>
                <a href="mailto:michelbergertsitsok@gmail.com" className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors">
                  <Mail className="h-4 w-4 text-primary" />
                  <span>michelbergertsitsok@gmail.com</span>
                </a>
              </div>

              <div className="flex gap-3 mb-6">
                <a href="https://github.com/Tsitsok98" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 rounded-full bg-secondary text-muted-foreground hover:text-foreground transition-all text-sm">
                  <Github className="h-4 w-4" />GitHub
                </a>
                <a href="https://www.facebook.com/michelbergertsitsok" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 rounded-full bg-secondary text-muted-foreground hover:text-foreground transition-all text-sm">
                  <Facebook className="h-4 w-4" />Facebook
                </a>
              </div>

              <Link to="/contact">
                <Button className="rounded-full px-6 py-5" data-testid="about-contact-btn">
                  {t("hero.contactBtn")}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-24 bg-card/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10 md:mb-16">
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-3 md:mb-4">{t("about.values")}</h2>
            <p className="text-sm md:text-base text-muted-foreground max-w-2xl mx-auto">{t("about.valuesSubtitle")}</p>
          </motion.div>

          <div className="grid sm:grid-cols-3 gap-6">
            {values.map((value, index) => (
              <motion.div key={value.titleKey} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="text-center p-6 rounded-2xl bg-background border border-border">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <value.icon className="h-7 w-7 text-primary" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-foreground mb-2">{t(value.titleKey)}</h3>
                <p className="text-sm text-muted-foreground">{t(value.descKey)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10 md:mb-16">
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-3 md:mb-4">{t("about.journey")}</h2>
            <p className="text-sm md:text-base text-muted-foreground max-w-2xl mx-auto">{t("about.journeySubtitle")}</p>
          </motion.div>
        </div>
      </section>

      <section className="py-12 md:py-24 bg-card/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-3 md:mb-4">{t("about.ready")}</h2>
            <p className="text-sm md:text-base text-muted-foreground mb-6 md:mb-8 max-w-xl mx-auto">{t("about.readySubtitle")}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/contact">
                <Button className="w-full sm:w-auto rounded-full px-6 py-5">
                  {t("hero.contactBtn")}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/portfolio">
                <Button variant="outline" className="w-full sm:w-auto rounded-full px-6 py-5">{t("about.viewPortfolio")}</Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
